import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { useEffect, useState } from "react";
import { IBenefitSelectionRule } from "types/Account/Account";

import { normaliseBenefitNameSynonyms } from "../../helpers/normaliseBenefitNameSynonyms";
import { KeywordChips, withPending } from "../BenefitSelection/KeywordChips";
import { filterStatesSomething } from "../BenefitSelection/mapper";
import { Surface } from "../BenefitSelection/Surface";

export const networkStatusOptions = [
  { name: "In network", value: "InNetwork" },
  { name: "Out of network", value: "OutOfNetwork" },
];

export const emptyRule = (): IBenefitSelectionRule => ({
  payerIds: [],
  planKeywords: [],
  matchKeywords: [],
  excludeKeywords: [],
  networkStatus: "InNetwork",
  // CLE-2755 — null, not 0. A blank override field means "read the payer's answer"; a typed 0 means
  // "this payer charges nothing", and seeding the form with 0 would make every new filter claim it.
  flatCopayOverride: null,
  flatCopayOverrideServiceTypes: [],
});

interface IBenefitSelectionRuleDialogProps {
  visible: boolean;
  /** The rule being edited, or a blank one when adding. */
  value: IBenefitSelectionRule;
  payerOptions: Array<{ label: string; value: number }>;
  loadingPayers: boolean;
  isNew: boolean;
  onSave: (rule: IBenefitSelectionRule) => void;
  onHide: () => void;
}

/**
 * CLE-2683 — one benefit rule, edited in isolation.
 *
 * The controls used to sit inline, one bordered card per rule, which stopped scaling at about
 * three: accounts are heading for a rule per payer. The list is a table now and the editing happens
 * here, matching how the contract adjustment rules are managed.
 *
 * 🚨 THERE IS NO TIER FIELD AND THERE MUST NOT BE ONE AGAIN. A tier is this facility's standing in
 * a payer's tiered network — one fact about the account, set once on the service line
 * (`TierPreferenceControl`) — not something a per-payer filter should restate. It never worked here
 * for the engine either: the filter narrowed the lines to Tier 1, the engine's own tier step then
 * looked for Tier 2, found nothing and fell through to the summary. `BenefitSelectionRule
 * .tierPreference` still exists and legacy still reads it, but nothing sends it any more and the
 * API writes `null` whatever arrives.
 *
 * Edits are held locally and only handed back on Apply, so cancelling genuinely discards them
 * instead of leaving a half-edited rule in the form.
 */
export function BenefitSelectionRuleDialog({
  visible,
  value,
  payerOptions,
  loadingPayers,
  isNew,
  onSave,
  onHide,
}: IBenefitSelectionRuleDialogProps) {
  const [draft, setDraft] = useState<IBenefitSelectionRule>(value);
  /** Keyword text typed and not yet a chip — see `KeywordChips`. Never silently discarded. */
  const [pending, setPending] = useState<Record<string, string>>({});
  // ⚠️ SHOWN ON THE ATTEMPT, NOT BEFORE IT. A dialog that opens already complaining about a form
  // nobody has filled in yet teaches an editor to read past its own warnings.
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setDraft(value);
    setAttempted(false);
    setPending({});
  }, [visible, value]);

  const set = (patch: Partial<IBenefitSelectionRule>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const setKeywords = (key: string, values: string[], text: string) => {
    // Normalised with the same rule the API applies, so a keyword that will not survive the save is
    // not left on screen looking saved.
    set({ [key]: normaliseBenefitNameSynonyms(values) } as any);
    setPending((current) => ({ ...current, [key]: text }));
  };

  const chipField = (
    key: "planKeywords" | "matchKeywords" | "excludeKeywords",
    label: string,
    placeholder: string
  ) => (
    <div className="field col-12">
      <Label htmlFor={`benefitSelectionRuleDialog.${key}`}>{label}</Label>
      <KeywordChips
        id={`benefitSelectionRuleDialog.${key}`}
        testId={`benefitSelectionRuleDialog.${key}`}
        value={draft[key] ?? []}
        placeholder={placeholder}
        onChange={(values, text) => setKeywords(key, values, text)}
      />
    </div>
  );

  // CLE-2792 — A FILTER THAT NARROWS NOTHING IS REFUSED. One was saved on dev in August: no payers,
  // no plans, no keywords. It matches every check, sits somewhere in an ordered list and changes
  // nothing — which is indistinguishable from a filter that was never saved, so nobody looks for it.
  // The same predicate the card's save guard uses, stated here at the point of typing.
  /** The filter as it really stands, including keyword text still sitting in an input. */
  const committed: IBenefitSelectionRule = {
    ...draft,
    planKeywords: withPending(draft.planKeywords, pending.planKeywords),
    matchKeywords: withPending(draft.matchKeywords, pending.matchKeywords),
    excludeKeywords: withPending(
      draft.excludeKeywords,
      pending.excludeKeywords
    ),
  };

  const narrowsSomething = filterStatesSomething(committed);

  return (
    <Dialog
      header={isNew ? "Add filter" : "Edit filter"}
      visible={visible}
      onHide={onHide}
      style={{ width: "min(46rem, 95vw)" }}
      data-testid="benefitSelectionRuleDialog"
      footer={
        <div className="flex justify-content-end gap-2">
          <Button
            type="button"
            label="Cancel"
            className="p-button-text p-button-sm"
            data-testid="benefitSelectionRuleDialog.cancel"
            onClick={onHide}
          />
          <Button
            className="p-button-sm"
            type="button"
            label={isNew ? "Add filter" : "Apply"}
            data-testid="benefitSelectionRuleDialog.save"
            onClick={() => {
              setAttempted(true);
              if (narrowsSomething) onSave(committed);
            }}
          />
        </div>
      }
    >
      <Surface className="formgrid grid">
        <div className="field col-12 md:col-6">
          <Label htmlFor="benefitSelectionRuleDialog.payerIds">Payers</Label>
          <MultiSelect
            id="benefitSelectionRuleDialog.payerIds"
            data-testid="benefitSelectionRuleDialog.payerIds"
            className="w-full"
            display="chip"
            filter
            options={payerOptions}
            disabled={loadingPayers}
            value={draft.payerIds ?? []}
            placeholder="All payers"
            onChange={(e) => set({ payerIds: e.value ?? [] })}
          />
        </div>

        <div className="field col-12 md:col-6">
          <Label htmlFor="benefitSelectionRuleDialog.planKeywords">
            Plans / products (optional)
          </Label>
          <KeywordChips
            id="benefitSelectionRuleDialog.planKeywords"
            testId="benefitSelectionRuleDialog.planKeywords"
            value={draft.planKeywords ?? []}
            placeholder="CHOICE PLUS"
            onChange={(values, text) =>
              setKeywords("planKeywords", values, text)
            }
          />
        </div>

        {chipField("matchKeywords", "Prefer lines containing", "FLEX PROVIDER")}
        {chipField(
          "excludeKeywords",
          "Never use lines containing",
          "OTHER PLAN PROVIDER"
        )}

        <div className="col-12 -mt-2 mb-2">
          <span
            className="text-xs text-color-secondary"
            data-testid="benefitSelectionRuleDialog.keywordHint"
          >
            Matched anywhere in a line&apos;s wording — its messages, its
            coverage description, the name of any PPO network the payer ties it
            to (ST. LUKES HOSPITAL (NAP RATES)) and its place of service
            (Office, Outpatient Hospital) — ignoring case. Use the distinctive
            part (FLEX PROVIDER, not NETWORK). If no line contains a preferred
            keyword, every line stays.
          </span>
        </div>

        <div className="field col-12 md:col-6">
          <Label htmlFor="benefitSelectionRuleDialog.networkStatus">
            Network status
          </Label>
          <Dropdown
            id="benefitSelectionRuleDialog.networkStatus"
            data-testid="benefitSelectionRuleDialog.networkStatus"
            className="w-full"
            optionLabel="name"
            options={networkStatusOptions}
            value={draft.networkStatus ?? "InNetwork"}
            onChange={(e) => set({ networkStatus: e.value })}
          />
        </div>

        {attempted && !narrowsSomething && (
          <div className="col-12">
            <span
              className="text-sm text-red-600"
              data-testid="benefitSelectionRuleDialog.emptyWarning"
            >
              This filter narrows nothing — no payers, plans, keywords or
              network status. Give it something to match before saving.
            </span>
          </div>
        )}
      </Surface>
    </Dialog>
  );
}
